import { UUID } from 'crypto'
import Teacher from './Teacher'
import Administrator from './Administrator'
import Student from './Student'
import { UserDto } from '../../pages/users/api/dto/get/UserDto'
import { UserInfo } from './UserInfo'
import { StudentUI } from '../../pages/users/model/StudentUI'
import { parseDate } from '../../common/utils/date-utils'
import Licenses from '../license/Licenses'
import PaymentTransactions from '../payment/PaymentTransactions'
import Language from './Language'

class User {
  private constructor(
    id: UUID,
    email: string,
    firstName: string,
    lastName: string,
    fatherName: string | undefined,
    address: string | undefined,
    phoneNumber: string | undefined,
    mobileNumber: string | undefined,
    birthday: Date,
    idNumber: string,
    ssrNumber: string,
    vatNumber: string,
    visuallyImpaired: boolean,
    language: Language,
    sound: boolean,
    student: Student | undefined,
    teacher: Teacher | undefined,
    administrator: Administrator | undefined,
  ) {
    this.id = id
    this.email = email
    this.firstName = firstName
    this.lastName = lastName
    this.fatherName = fatherName ? fatherName : ''
    this.address = address ? address : ''
    this.phoneNumber = phoneNumber ? phoneNumber : ''
    this.mobileNumber = mobileNumber ? mobileNumber : ''
    this.birthday = birthday
    this.idNumber = idNumber
    this.ssrNumber = ssrNumber
    this.vatNumber = vatNumber
    this.visuallyImpaired = visuallyImpaired
    this.language = language
    this.sound = sound
    this.student = student
    this.teacher = teacher
    this.administrator = administrator
  }

  private readonly id: UUID
  private readonly email: string
  private readonly firstName: string
  private readonly lastName: string
  private readonly fatherName: string
  private readonly address: string
  private readonly phoneNumber: string
  private readonly mobileNumber: string
  private readonly birthday: Date
  private readonly idNumber: string
  private readonly ssrNumber: string
  private readonly vatNumber: string
  private readonly visuallyImpaired: boolean
  private readonly language: Language
  private readonly sound: boolean
  private readonly student?: Student
  private readonly teacher?: Teacher
  private readonly administrator?: Administrator

  getId(): UUID {
    return this.id
  }

  getInfo(): UserInfo {
    return {
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      fatherName: this.fatherName,
      address: this.address,
      phoneNumber: this.phoneNumber,
      mobileNumber: this.mobileNumber,
      birthday: this.birthday,
      idNumber: this.idNumber,
      ssrNumber: this.ssrNumber,
      vatNumber: this.vatNumber,
      visuallyImpaired: this.visuallyImpaired,
      language: this.language,
      sound: this.sound,
    } satisfies UserInfo
  }

  findByStudentId(studentId: UUID): User | undefined {
    return this.student?.hasId(studentId) ? this : undefined
  }

  getStudentLicenses(): Licenses {
    if (this.student !== undefined) {
      return this.student.getLicenses()
    }

    throw Error('Not student')
  }

  getTransactions(): PaymentTransactions {
    if (this.student !== undefined) {
      return this.student.getTransactions()
    }

    throw new Error('Not student')
  }

  isStudent(): boolean {
    return this.student !== undefined
  }

  toStudentUI(): StudentUI | undefined {
    if (this.student === undefined) {
      return undefined
    }

    return this.student.toStudentUI(this.getInfo())
  }

  getStudentId(): UUID {
    if (this.student !== undefined) {
      return this.student.getId()
    }

    throw new Error('Not student')
  }

  static fromDto(userDto: UserDto): User {
    const {
      id,
      email,
      firstName,
      lastName,
      fatherName,
      address,
      phoneNumber,
      mobileNumber,
      birthday,
      idNumber,
      ssrNumber,
      vatNumber,
      visuallyImpaired,
      language,
      sound,
    } = userDto

    const student =
      userDto.student != null ? Student.fromDto(id, userDto.student) : undefined
    const teacher =
      userDto.teacher != null ? Teacher.fromDto(id, userDto.teacher) : undefined
    const administrator =
      userDto.administrator != null
        ? Administrator.fromDto(id, userDto.administrator)
        : undefined

    return new User(
      id,
      email,
      firstName,
      lastName,
      fatherName,
      address,
      phoneNumber,
      mobileNumber,
      parseDate(birthday),
      idNumber,
      ssrNumber,
      vatNumber,
      visuallyImpaired,
      language,
      sound,
      student,
      teacher,
      administrator,
    )
  }
}

export default User
